'use client';

import { useState, useEffect, useRef } from 'react';
import { cn } from '@/src/shared/lib/utils';
import { useChatStore } from '../model/useChatStore';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import { chatSocketService } from '@/src/shared/services/chat-socket.service';
import { ClaimedTag } from '@/src/shared/ui/tags/ClaimedTag';
import { FileUploadButton } from './FileUploadButton';
import { AttachmentPreview } from './AttachmentPreview';
import { buildApiUrl } from '@/src/shared/api/config';
import { OrderCard } from './OrderCard';
import type { Message } from '../types';

interface MiniChatMessagesProps {
  conversationId: string;
  className?: string;
}

/**
 * Mini Chat Messages - Displays messages for selected conversation in mini widget
 *
 * Features:
 * - Claimed tag at top
 * - Messages with bubble styling
 * - Date separator ("Today")
 * - Input field with attachment and send buttons
 */
export const MiniChatMessages = ({
  conversationId,
  className,
}: MiniChatMessagesProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(0);

  const activeActor = useAuthStore((state) => state.activeActor);

  const {
    getMessages,
    loadMessages,
    sendMessage,
    markAsRead,
    joinConversation,
    isLoadingMessages,
    getTypingActors,
    connectionStatus,
  } = useChatStore();

  const messages = getMessages(conversationId);
  const isLoading = isLoadingMessages[conversationId];
  const typingActors = getTypingActors(conversationId);

  // Load messages when conversation is selected
  useEffect(() => {
    loadMessages(conversationId);
  }, [conversationId, loadMessages]);

  // Join conversation room when connected
  useEffect(() => {
    if (connectionStatus === 'connected') {
      joinConversation(conversationId).catch((err) => {
        console.error('[MiniChatMessages] Failed to join conversation:', err);
      });
    }
  }, [conversationId, connectionStatus, joinConversation]);

  // Initial scroll to bottom when chat is first opened
  useEffect(() => {
    if (!messagesContainerRef.current || isLoading || messages.length === 0) return;

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'instant',
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isLoading, conversationId]); // Only run when conversation changes or loading finishes

  // Auto-scroll on new messages
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;

    const newMessagesAdded = messages.length > prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;

    if (!newMessagesAdded) return;

    const lastMessage = messages[messages.length - 1];
    const isSentByMe = lastMessage?.senderActorId === activeActor?.id;

    if (isSentByMe || isNearBottom) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, activeActor]);

  // Mark messages as read
  useEffect(() => {
    if (messages.length === 0 || !activeActor) return;

    const unreadMessages = messages.filter(
      (msg) => msg.senderActorId !== activeActor.id
    );

    if (unreadMessages.length > 0) {
      const lastMessage = unreadMessages[unreadMessages.length - 1];
      markAsRead(conversationId, lastMessage.id);
    }
  }, [conversationId, messages, markAsRead, activeActor]);

  // Handle typing indicator
  const handleTyping = () => {
    if (inputValue.trim()) {
      chatSocketService.typing(conversationId);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!inputValue.trim()) {
        chatSocketService.stopTyping(conversationId);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [inputValue, conversationId]);

  // Upload file
  const uploadFile = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversationId', conversationId);

      const response = await fetch(buildApiUrl('/chat/attachments'), {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const data = await response.json();
      return data.data.id;
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    }
  };

  // Handle send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSending) return;

    const body = inputValue.trim();

    // Upload pending files first
    let attachmentIds: string[] | undefined;
    if (pendingFiles.length > 0) {
      setIsSending(true);

      const uploadPromises = pendingFiles.map(uploadFile);
      const uploadedIds = await Promise.all(uploadPromises);
      const validIds = uploadedIds.filter((id) => id !== null) as string[];

      attachmentIds = validIds.length > 0 ? validIds : undefined;
      setPendingFiles([]);
    }

    if (!body && (!attachmentIds || attachmentIds.length === 0)) {
      setIsSending(false);
      return;
    }

    if (body.length > 2000) {
      alert('Message is too long (max 2000 characters)');
      setIsSending(false);
      return;
    }

    setIsSending(true);
    setInputValue('');

    try {
      await sendMessage(conversationId, body, attachmentIds);

      if (chatSocketService.isConnected()) {
        chatSocketService.stopTyping(conversationId);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setInputValue(body);
    } finally {
      setIsSending(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Handle file selection
  const handleFilesSelected = (files: File[]) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  // Group messages by date
  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { date: string; messages: Message[] }[] = [];
    let currentDate = '';

    messages.forEach((message) => {
      const messageDate = new Date(message.createdAt).toDateString();
      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({ date: messageDate, messages: [message] });
      } else {
        groups[groups.length - 1].messages.push(message);
      }
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className={cn('flex-1 flex flex-col overflow-hidden', className)}>
      {/* Claimed tag */}
      <div className="flex justify-end px-5 py-2">
        <ClaimedTag />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-text-secondary">Loading messages...</span>
        </div>
      )}

      {/* Messages */}
      {!isLoading && (
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto px-5"
        >
          {messages.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center h-full">
              <span className="text-text-secondary">No messages yet</span>
              <span className="text-text-secondary text-sm mt-2">
                Send a message to start the conversation
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5 py-4">
              {messageGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="flex flex-col gap-2.5">
                  {/* Date separator */}
                  <div className="flex justify-center">
                    <div className="h-8 px-7 rounded-full border border-accent-secondary flex justify-center items-center">
                      <span className="text-text-primary text-base font-normal font-['Roboto'] leading-5">
                        {formatDateLabel(group.date)}
                      </span>
                    </div>
                  </div>

                  {/* Messages in group */}
                  {group.messages.map((message) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      isOwn={activeActor?.id === message.senderActorId}
                    />
                  ))}
                </div>
              ))}

              {/* Typing indicator */}
              {typingActors.length > 0 && (
                <div className="text-sm text-text-secondary italic pl-4">
                  Typing...
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Input area */}
      <div className="px-5 pb-5">
        <form onSubmit={handleSendMessage}>
          <div className="flex items-end gap-2.5">
            {/* Input container */}
            <div className="flex-1 rounded-[20px] shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)] border border-background-secondary bg-background-primary">
              <div className="flex items-center p-3.5">
                <textarea
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    handleTyping();
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className={cn(
                    'flex-1 bg-transparent',
                    'text-text-primary text-base font-normal',
                    'border-0 outline-none resize-none',
                    'placeholder:text-text-secondary',
                    'min-h-[24px] max-h-44'
                  )}
                  rows={1}
                  disabled={isSending}
                  maxLength={2000}
                />

                {/* Attachment button */}
                <div className="ml-2">
                  <FileUploadButton
                    onFilesSelected={handleFilesSelected}
                    disabled={isSending}
                    maxFiles={10}
                    maxSizeMB={5}
                  />
                </div>
              </div>

              {/* Attachments preview */}
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 px-3.5 pb-3.5">
                  {pendingFiles.map((file, index) => (
                    <AttachmentPreview
                      key={`${file.name}-${index}`}
                      file={file}
                      onRemove={() => {
                        setPendingFiles((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Send button */}
            <button
              type="submit"
              disabled={
                (!inputValue.trim() && pendingFiles.length === 0) || isSending
              }
              className={cn(
                'w-14 h-14 rounded-full flex items-center justify-center transition-colors flex-shrink-0',
                (inputValue.trim() || pendingFiles.length > 0) && !isSending
                  ? 'bg-accent-primary hover:bg-accent-primary/90'
                  : 'bg-gray-300 cursor-not-allowed'
              )}
            >
              {isSending ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="transform rotate-45"
                >
                  <path
                    d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Message Bubble Component
 */
interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
}

const MessageBubble = ({ message, isOwn }: MessageBubbleProps) => {
  const time = new Date(message.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // Handle SYSTEM messages
  if (message.type === 'SYSTEM') {
    return (
      <div className="flex justify-center">
        <div className="px-4 py-2 bg-background-secondary rounded-full">
          <span className="text-text-secondary text-sm italic">
            {message.body}
          </span>
        </div>
      </div>
    );
  }

  // Handle ORDER_CARD messages
  if (message.type === 'ORDER_CARD' && message.metadata) {
    const metadata = message.metadata as {
      order?: {
        id: string;
        title: string;
        description?: string;
        location?: string;
        deadline?: string;
        startedAt?: string;
      };
      participant?: {
        id: string;
        name: string;
        displayName?: string;
        avatarUrl?: string;
      };
    };

    return (
      <div className={cn('flex justify-center')}>
        {metadata.order && metadata.participant ? (
          <OrderCard
            order={metadata.order}
            participant={metadata.participant}
            onLocationClick={() => {
              if (metadata.order?.location) {
                console.log('View location:', metadata.order.location);
              }
            }}
          />
        ) : (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <span className="text-yellow-800 text-base">
              Order data unavailable
            </span>
          </div>
        )}
      </div>
    );
  }

  // Handle TEXT messages
  return (
    <div
      className={cn(
        'flex flex-col gap-[5px]',
        isOwn ? 'items-end' : 'items-start'
      )}
    >
      <div
        className={cn(
          'max-w-[384px] px-4 py-2 flex items-start gap-2.5',
          isOwn
            ? 'bg-background-secondary rounded-tl-[10px] rounded-bl-2xl rounded-br-[10px]'
            : 'bg-background-tertiary rounded-tr-[10px] rounded-bl-[10px] rounded-br-2xl'
        )}
      >
        {/* Message text */}
        <span className="flex-1 text-text-primary text-base font-normal font-['Roboto'] leading-5 whitespace-pre-wrap">
          {message.body}
          {message.isEdited && (
            <span className="text-text-secondary text-sm ml-1">(edited)</span>
          )}
        </span>

        {/* Time */}
        <span className="text-text-secondary text-base font-normal font-['Roboto'] leading-5 flex-shrink-0">
          {time}
        </span>
      </div>

      {/* Attachments */}
      {message.attachments && message.attachments.length > 0 && (
        <div className={cn('flex flex-col gap-2', isOwn ? 'items-end' : 'items-start')}>
          {message.attachments.map((ma) => {
            const att = ma.attachment;
            const isImage = att.mimeType.startsWith('image/');

            return (
              <a
                key={ma.id}
                href={buildApiUrl(`/chat/attachments/${att.id}/url`)}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'block max-w-[200px] rounded-lg overflow-hidden',
                  'border border-border-primary hover:opacity-90 transition-opacity'
                )}
              >
                {isImage ? (
                  <img
                    src={buildApiUrl(`/chat/attachments/${att.id}/url`)}
                    alt={att.fileName}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex items-center gap-2 p-2 bg-background-secondary">
                    <span className="text-2xl">📄</span>
                    <span className="text-sm truncate">{att.fileName}</span>
                  </div>
                )}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Format date label for separator
 */
function formatDateLabel(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}
